/**
 * Bewegliche Teile: Autos und Hoteleingaenge.
 *
 * Ein Eingang steht an der Strassenkante des Grundstuecks, zur Fahrbahn gewandt -
 * genau wie das gelbe Kunststoffteil der Vorlage. Er markiert das Feld, an dem
 * Uebernachtungskosten faellig werden.
 */
import * as THREE from 'three'
import { FELDER, NACH_INNEN, autoPunkt, eingangsPunkt } from '../core/data/spielplan'
import type { HotelId } from '../core/data/hotels'
import { HOTELS } from '../core/data/hotels'
import { EBENE } from './ebenen'
import { quader, verbinde, walmdach } from './formen'

/** Ein Auto: flacher Koerper, aufgesetztes Dach, vier Raeder. */
export function baueAuto(farbe: string): THREE.Group {
  const laenge = 20
  const breite = 9.5
  const teile: THREE.BufferGeometry[] = [
    quader(laenge, breite, 1.6, 5.2),
    (() => {
      const kabine = quader(laenge * 0.5, breite * 0.86, 5.2, 8.4)
      kabine.translate(-laenge * 0.06, 0, 0)
      return kabine
    })(),
  ]
  const koerper = new THREE.Mesh(
    verbinde(teile),
    new THREE.MeshStandardMaterial({ color: farbe, roughness: 0.32, metalness: 0.28 }),
  )

  const raeder: THREE.BufferGeometry[] = []
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const rad = new THREE.CylinderGeometry(2.4, 2.4, 1.8, 14)
      rad.rotateX(Math.PI / 2)
      rad.translate(sx * laenge * 0.3, 2.4, sz * (breite / 2 - 0.2))
      raeder.push(rad)
    }
  }
  const reifen = new THREE.Mesh(
    verbinde(raeder),
    new THREE.MeshStandardMaterial({ color: '#1b1d22', roughness: 0.9 }),
  )

  const gruppe = new THREE.Group()
  for (const m of [koerper, reifen]) {
    m.castShadow = true
    gruppe.add(m)
  }
  return gruppe
}

/** Stellt ein Auto auf ein Feld, mehrere nebeneinander. */
export function stelleAuto(
  auto: THREE.Object3D,
  feldIndex: number,
  platz: number,
  plaetze: number,
): void {
  const feld = FELDER[feldIndex]!
  const p = autoPunkt(feldIndex, platz, plaetze)
  auto.position.set(p.x, EBENE.auto, p.z)
  auto.rotation.y = -Math.atan2(feld.richtung.z, feld.richtung.x)
}

/** Ein Hoteleingang: Treppe mit Vordach in der Hausfarbe. */
export function baueEingang(hotel: HotelId, feldIndex: number): THREE.Group {
  const breite = 21
  const tiefe = 12
  const stufen: THREE.BufferGeometry[] = []
  for (let i = 0; i < 3; i++) {
    const s = quader(breite - i * 3.2, tiefe - i * 2.6, i * 1.3, (i + 1) * 1.3)
    s.translate(0, 0, (i * 2.6) / 2)
    stufen.push(s)
  }
  const treppe = new THREE.Mesh(
    verbinde(stufen),
    new THREE.MeshStandardMaterial({ color: '#f0c539', roughness: 0.45 }),
  )
  const vordach = new THREE.Mesh(
    walmdach(breite * 0.92, tiefe * 0.82, 5.4, 3.4, 0.5),
    new THREE.MeshStandardMaterial({ color: HOTELS[hotel].farbe, roughness: 0.6 }),
  )
  const pfosten: THREE.BufferGeometry[] = []
  for (const sx of [-1, 1]) {
    const p = quader(1.6, 1.6, 0, 5.4)
    p.translate(sx * (breite * 0.38), 0, -tiefe * 0.26)
    pfosten.push(p)
  }
  const stuetzen = new THREE.Mesh(
    verbinde(pfosten),
    new THREE.MeshStandardMaterial({ color: '#d9b02f', roughness: 0.5 }),
  )

  const gruppe = new THREE.Group()
  gruppe.name = `eingang-${hotel}-${feldIndex}`
  for (const m of [treppe, vordach, stuetzen]) {
    m.castShadow = true
    m.receiveShadow = true
    gruppe.add(m)
  }

  const feld = FELDER[feldIndex]!
  const p = eingangsPunkt(feldIndex, hotel)
  gruppe.position.set(p.x, EBENE.eingang, p.z)
  // Zur Fahrbahn gewandt: die Treppe zeigt zur Strassenmitte.
  const zurStrasse = feld.innen === hotel ? -NACH_INNEN : NACH_INNEN
  gruppe.rotation.y = -Math.atan2(feld.normale.z * zurStrasse, feld.normale.x * zurStrasse)
  return gruppe
}
